/**
 * MAS Direct template formulas (implementations).
 * Direct-only COGS and pricing formulas; shared formulas remain in shared/formulas.
 */

import { getShortCodeFromFullName } from '../../utils/gsmsHandlingTypeUtils';

export const calculateWACForDirectBid = (suggestedBidPrice: number): number => {
  return suggestedBidPrice * 1.01;
};

export const calculateGSMSBottleCapacity = (
  gsmsHandlingType: string,
  gsmsPackSize: number
): string => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'MI') return '';
  if (gsmsPackSize > 1 && gsmsPackSize < 181) return '120cc';
  if (gsmsPackSize > 180 && gsmsPackSize < 501) return '500cc';
  if (gsmsPackSize > 500) return '950cc';
  if (gsmsPackSize <= 1) return '';
  return '';
};

export const calculateGSMSBottleCost = (
  gsmsHandlingType: string,
  gsmsBottleCapacity: string
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode !== 'R') return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.084;
    case '500cc': return 0.238;
    case '950cc': return 0.572;
    default: return 0;
  }
};

export const calculateGSMSCapCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.042;
    case '500cc': return 0.073;
    case '950cc': return 0.096;
    default: return 0;
  }
};

export const calculateGSMSLabelCost = (
  gsmsHandlingType: string,
  gsmsBottleCost: number
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'OL') return 0.15;
  if (gsmsBottleCost === 0) return 0;
  return 0.03;
};

export const calculateGSMSInsertCost = (
  gsmsBottleCost: number,
  description: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  if (description === 'Apotex') return 0.15;
  return 0.07;
};

export const calculateGSMSBoxCartonCost = (
  gsmsBottleCost: number,
  gsmsBottleCapacity: string
): number => {
  if (gsmsBottleCost === 0) return 0;
  switch (gsmsBottleCapacity) {
    case '120cc': return 0.009;
    case '500cc': return 0.039;
    case '950cc': return 0.127;
    default: return 0;
  }
};

export const calculateOutsourceRepackFee = (gsmsHandlingType: string): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  return shortCode === 'PL2' ? 0.33 : 0;
};

export const calculateGSMSFrtCost = (
  gsmsBottleCapacity: string,
  outsourceRepackFee: number
): number => {
  if (gsmsBottleCapacity === '' || gsmsBottleCapacity === ' ') return 0;
  if (outsourceRepackFee === 0) {
    return gsmsBottleCapacity === '120cc' ? 0.1059 : 0.26475;
  }
  return 0;
};

export const calculatePV2Percent = (
  gsmsBottleCapacity: string,
  wac: number
): number => {
  if (!gsmsBottleCapacity || gsmsBottleCapacity.trim() === '' || gsmsBottleCapacity === ' ') return 0;
  if (!wac || isNaN(wac) || wac <= 0) return 0;
  return wac * 0.02;
};

export const calculateGSMSLabor = (
  gsmsHandlingType: string,
  suggestedBidPrice: number
): number => {
  const shortCode = getShortCodeFromFullName(gsmsHandlingType);
  if (shortCode === 'PL' || shortCode === 'PL2') return 0.02;
  if (shortCode === 'MI') return 0;
  if (shortCode === 'OL' || shortCode === 'R') {
    if (!suggestedBidPrice || suggestedBidPrice <= 0) return 0;
    let calculatedValue = suggestedBidPrice * 0.0127;
    calculatedValue = Math.max(calculatedValue, 0.07);
    calculatedValue = Math.min(calculatedValue, 3);
    return calculatedValue;
  }
  return 0;
};

export const calculateGSMSCost = (data: {
  gsmsNetCostFromMfg: number;
  mfgBottleSize: number;
  gsmsPackSize: number;
  gsmsBottleCost: number;
  gsmsCapCost: number;
  gsmsLabelCost: number;
  gsmsInsertCost: number;
  gsmsIffFee: number;
  gsmsBoxCartonCost: number;
  gsmsDesiccantCost: number;
  outsourceRepackFee: number;
  blendedWhlsFee: number;
  gsmsFrtCost: number;
  pv2Percent: number;
  gsmsLabor: number;
}): number => {
  const baseCost = (data.gsmsNetCostFromMfg / data.mfgBottleSize) * data.gsmsPackSize;
  const additionalCosts =
    data.gsmsBottleCost +
    data.gsmsCapCost +
    data.gsmsLabelCost +
    data.gsmsInsertCost +
    data.gsmsIffFee +
    data.gsmsBoxCartonCost +
    data.gsmsDesiccantCost +
    data.outsourceRepackFee +
    data.blendedWhlsFee +
    data.gsmsFrtCost +
    data.pv2Percent +
    data.gsmsLabor;
  return baseCost + additionalCosts;
};
